import { useEffect, useState } from 'react';

import { BootSplash } from '@/components/ui/BootSplash';
import MiningLoader from '@/components/ui/mining-loader';
import { FirstLogin } from '@/components/screens/FirstLogin';
import { Login } from '@/components/screens/Login';
import { Shell } from '@/components/screens/Shell';
import { Icon } from '@/components/ui/Icon';
import { useSession } from '@/store/session';

/**
 * Four phases: boot, sign-in, the one-time first-login form, then the game.
 * Everything below Shell assumes `user` is non-null, which is why the gate
 * lives here rather than being re-checked in every screen.
 */
export function App() {
  const phase = useSession((s) => s.phase);
  const error = useSession((s) => s.error);
  const init = useSession((s) => s.init);

  /*
   * The splash plays over whatever is happening underneath, once per page load.
   *
   * It is mounted alongside `init()` rather than before it, so the two overlap: the
   * cards are covering a wait that was already occurring instead of adding one in
   * front of it. `MiningLoader` only becomes visible if boot is *still* going when
   * the cards end, which is exactly the case where a progress bar is worth showing.
   */
  const [splashDone, setSplashDone] = useState(false);

  useEffect(() => { void init(); }, [init]);

  /* Even a failed boot gets its splash finished first — the error screen appearing
     mid-logo reads as a crash rather than as a server that said no. */
  if (!splashDone) {
    return (
      <>
        {/*
          Keyed, and the keys are the point.

          Without them React reconciles these two by position, and child 0 flipping
          between `<MiningLoader />` and `null` the moment boot finishes remounts the
          splash sitting behind it — restarting its sequence and re-running its
          preloads. A mount trace showed exactly that: MOUNT, UNMOUNT, MOUNT, MOUNT.
          Keys make the identity explicit, so the loader can come and go underneath a
          splash that is unaffected by it.
        */}
        {phase === 'booting' ? <MiningLoader key="loader" /> : null}
        <BootSplash
          key="splash"
          ready={phase !== 'booting'}
          onDone={() => setSplashDone(true)}
        />
      </>
    );
  }

  if (phase === 'error') {
    return (
      <div className="grid h-full place-items-center p-6 text-center">
        <div>
          <Icon name="cave" size={48} className="mx-auto mb-3 text-ink-dim" />
          <h1 className="font-pixel text-sm text-torch-core">THE CAVE IS QUIET</h1>
          <p className="mt-3 text-sm text-ink-dim">{error}</p>
          <button
            className="mt-5 rounded-sm border border-ui-lit bg-cave-floor px-5 py-3 font-bold"
            onClick={() => void init()}
          >
            Try again
          </button>
        </div>
      </div>
    );
  }

  if (phase === 'booting') return <MiningLoader />;
  if (phase === 'needs-login') return <Login />;
  if (phase === 'needs-first-login') return <FirstLogin />;
  return <Shell />;
}
